import * as Api from '@ViewModels';
import { Query, useQueries, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

export const SYSTEM_JOB_QUERY_KEY = 'SystemJob/<id>';

export function useGetSystemJobQuery<TSystemJob extends Api.ISystemJob = Api.ISystemJob>({
	enabled,
	jobId,
	impersonationContext,
	refetchOnWindowFocus = false,
	refetchInterval,
}: {
	enabled: boolean;
	jobId: string;
	/** When set, scopes the SystemJob request to the impersonated account (admin flows). */
	impersonationContext?: Api.IImpersonationContext;
	refetchInterval?:
		| number
		| false
		| ((query: Query<TSystemJob, Error, TSystemJob, string[]>) => number | false | undefined);
	refetchOnWindowFocus?: boolean;
}) {
	const userSession = useUserSession();

	return useQuery({
		enabled,
		queryFn: async () => {
			return userSession.webServiceHelper.callAsync<TSystemJob>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `SystemJob/${jobId}`,
					impersonationContext,
				}),
				'GET'
			);
		},
		queryKey: [SYSTEM_JOB_QUERY_KEY, jobId],
		refetchInterval,
		refetchOnWindowFocus,
	});
}

export function useGetSystemJobsQueries<TSystemJob extends Api.ISystemJob = Api.ISystemJob>({
	jobs,
	pollingIntervalMs = 3000,
	refetchOnWindowFocus = false,
}: {
	jobs: { enabled: boolean; jobId: string }[];
	/** Refetch interval in ms while job has no data or status is Processing. Set false to disable polling. */
	pollingIntervalMs?: number | false;
	refetchOnWindowFocus?: boolean;
}) {
	const userSession = useUserSession();

	return useQueries({
		queries: jobs.map(({ jobId, enabled }) => {
			const refetchInterval:
				| number
				| false
				| ((query: Query<TSystemJob, Error, TSystemJob, readonly unknown[]>) => number | false) =
				pollingIntervalMs === false
					? false
					: (query: Query<TSystemJob, Error, TSystemJob, readonly unknown[]>) => {
							const data = query.state.data;
							if (!jobId) {
								return false;
							}
							return !data || data?.status === 'Processing' ? pollingIntervalMs : false;
						};

			return {
				queryKey: [SYSTEM_JOB_QUERY_KEY, jobId] as const,
				queryFn: async () => {
					return userSession.webServiceHelper.callAsync<TSystemJob>(
						Api.ImpersonationBroker.composeApiUrl({
							urlPath: `SystemJob/${jobId}`,
						}),
						'GET'
					);
				},
				enabled: Boolean(jobId) && enabled,
				refetchInterval,
				refetchOnWindowFocus,
			};
		}),
	});
}
